/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef, ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import AccountCell from '@ion/applications/src/pages/admin/accounts/AccountCell';
import {dispatchGridColumnsChange} from '@ion/applications/src/store/helpers';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  CRUDPermissions,
  Entity,
  GenericObject,
  PlatformDeviceTypes
} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import CellTooltipWrapper from '@ion/dls/src/components/tooltip/CellTooltipWrapper';
import {
  definitionsToState,
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {AgGridType, IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {HEADER_HEIGHT, SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {formatUserDateTime} from '@ion/dls/src/utils/ComponentUtils';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import useHasRolePermission from '@/security/useHasRolePermission';
import {setGlobalGroup} from '@/store/actions/global-group-chooser';
import {
  NAME,
  SET_GRID_COLUMNS,
  updateGridColumns
} from '@/store/actions/jobsearchengine';
import {jobSearchEngineGridColumnStateSelector} from '@/store/selectors/jobsearchengine';
import JobDevicesMiniBarChart from './cells/JobDevicesMiniBarChart';
import JobIdCell from './cells/JobIdCell';
import JobStatusCell from './cells/JobStatusCell';
import {JobSearchEngineRowEditor} from './JobSearchEngineRowEditor';
import {
  formatFileTypes,
  getTasksDevicesCounts,
  sortModelConverter
} from './JobSearchEngineUtils';
import {useJobGridPagination} from './useJobGridPagination';
import {useJobPagination} from './useJobPagination';

export interface JobSearchEngineGridProps {
  isTabMaxLimitReached: boolean;
  handleJobClick: Function;
  Features?: ApplicationFeatures;
  refreshInitialization?: Function;
  activeTab: string;
  loadItems: (arg: {
    page?: number;
    pageSize?: number;
    sort?: string[] | undefined;
    filter?: GenericObject;
    callback: Function;
  }) => void;
  refresh: boolean;
  onRefresh: Function;
  columnState?: any;
  viewSubGroups?: boolean;
  toggleSubGroups?: (view: boolean) => void;
  ref?: RefObject<unknown>;
  setIsRerunFailed?: Function;
  jobsPermissions?: CRUDPermissions;
  deviceTypes?: PlatformDeviceTypes;
  tabPaging: UsePaginationResult;
}

const PAGINATION_BAR_HEIGHT = 97;

const useStyles = makeStyles(() => ({
  gridContainer : {
    flex                   : 1,
    display                : 'flex',
    flexDirection          : 'column',
    position               : 'relative',
    minHeight              : 0,
    '& > .ag-theme-quartz' : {
      flex : '1 !important'
    },
    // prevents footer from being squeezed by the grid's min-height...
    '& > div:last-child' : {
      flexShrink : 0
    }
  },
  miniBarChart : {
    width : '100%'
  },
  // prevents IonGrid's default fetching-state class from forcing 100vh...
  grid           : {},
  loadingOverlay : {
    position        : 'absolute',
    top             : HEADER_HEIGHT + 1,
    left            : 0,
    right           : 0,
    bottom          : PAGINATION_BAR_HEIGHT,
    backgroundColor : dlsColor('white'),
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    zIndex          : 10
  }
}));

export const defaultSort: SortModelType = {colId: 'jobId', sort: 'desc'};

export const attributeMappings = {
  jobId          : 'jobId',
  name           : 'name',
  description    : 'description',
  tasks          : 'tasks',
  devices        : 'devices',
  accountUI      : 'account',
  progressStatus : 'progressStatus',
  username       : 'username',
  fileTypes      : 'fileTypes',
  createdOn      : 'createdOn',
  updatedOn      : 'updatedOn'
};

const JobSearchEngineGrid = forwardRef(({
  isTabMaxLimitReached,
  setIsRerunFailed,
  Features,
  refreshInitialization,
  handleJobClick,
  viewSubGroups,
  toggleSubGroups,
  deviceTypes,
  jobsPermissions,
  activeTab,
  loadItems,
  refresh,
  onRefresh,
  tabPaging
}: JobSearchEngineGridProps, ref) => {
  const classes             = useStyles();
  const dispatch            = useDispatch();
  const columnState         = useSelector(jobSearchEngineGridColumnStateSelector);
  const gridRef             = useRef<IonGridAPI>(null);
  const gridApiRef          = useRef<AgGridType>(null);
  const silentRefreshRef    = useRef(false);
  const maxLimit            = SmartSearchPaging.MAX_LIMIT;
  const {getPaginationForTab} = useJobPagination();

  const paginationForJobTab                   = getPaginationForTab(activeTab);
  const [isContentLoading, setContentLoading] = useState(false);

  const accountsPermissions = {
    hasUpdatePermission : useHasRolePermission(Entity.account, Operation.Update)
  };

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const onSelectAccount = function(group) {
    dispatch(setGlobalGroup(group) as any);
  };

  const columnComponents = useMemo(() => ({
    statusRenderer : (data) => <JobStatusCell status={data?.value} />,
    fileTypesCell  : (item: GridResult) => (
      <CellTooltipWrapper data={formatFileTypes(item?.data?.task, deviceTypes)}
        gridApi={item?.api}
        columnName={'fileTypes'} />
    ),
    detailsRenderer : (data) => {
      const {
        total,
        completed,
        pending,
        failed,
        cancelled
      } = data?.value || {};
      return (<Render.If condition={!isNil(data?.value) && total !== cancelled}>
        <div className={classes.miniBarChart}>
          <JobDevicesMiniBarChart completed={completed}
            failed={failed}
            cancelled={cancelled}
            inProgress={pending}
            total={total}/>
        </div>
      </Render.If>);
    },
    jobIdRenderer : (data) => (
      <JobIdCell jobId={data?.value}
        disabled={isTabMaxLimitReached}
        status={data?.data?.progressStatus}
        handleClick={handleJobClick}/>
    ),
    accountName : (item: GridResult) => (
      <AccountCell item={item}
        isCtcAdmin
        Features={Features}
        accountsPermissions={accountsPermissions}
        refreshInitialization={refreshInitialization}
        onSelectAccount={onSelectAccount} />
    ),
    rowMenu : item => (
      <JobSearchEngineRowEditor job={item?.data}
        setIsRerunFailed={setIsRerunFailed}
        handleJobClick={handleJobClick}
        jobsPermissions={jobsPermissions}
        Features={Features}
        hasJob />
    )
  }), [
    handleJobClick,
    isTabMaxLimitReached,
    deviceTypes,
    Features,
    accountsPermissions,
    onSelectAccount,
    refreshInitialization
  ]);

  const constructGridRows = useCallback((items: GenericObject) => {
    return items?.map(item => ({
      jobId          : item.jobId,
      account        : item?.account,
      accountUI      : item?.account?.title,
      name           : item.name,
      id             : item?.jobId,
      description    : item.description,
      tasks          : item?.taskCount,
      task           : item?.tasks,
      devices        : item?.tasks?.[0]?.deviceCount,
      progressStatus : item.progressStatus,
      details        : getTasksDevicesCounts(item?.tasks),
      fileTypes      : formatFileTypes(item?.tasks, deviceTypes),
      username       : item.username || '-',
      createdOn      : formatUserDateTime(item.createdOn),
      updatedOn      : formatUserDateTime(item.updatedOn)
    })) as GridResult[];
  }, [deviceTypes]);

  const onRowDoubleClicked = useCallback((event) => {
    if (!isTabMaxLimitReached) {
      const {jobId, progressStatus} = event?.data;
      handleJobClick?.(jobId, progressStatus);
    }
  }, [handleJobClick, isTabMaxLimitReached]);

  const onToggleColumnChooser = useCallback(() => {
    toggleColumnChooser(true);
  }, []);

  const getGridHeight = useCallback(function() {
    return classes.grid;
  }, []);

  const getExportParams = function() {
    const ionGridData = gridRef?.current?.getExportParams() || {};
    const {columns}   = ionGridData || {};
    if (columns) {
      const definitions = {};
      columns.forEach(({headerName, field}) => {
        if (headerName) {
          const fieldLabel = attributeMappings[field] || field;
          definitions[fieldLabel] = headerName;
        }
      });
      return definitions;
    }
    return {};
  };

  const updateRowNodes = useCallback((items: GenericObject[], versionCheck = false) => (
    gridRef.current?.updateRowNodes?.(items, versionCheck)
  ), []);

  const refreshInfiniteCache = useCallback(() => {
    silentRefreshRef.current = true;
    gridRef.current?.refreshInfiniteCache?.();
  }, []);

  useImperativeHandle(ref, () => ({
    onToggleColumnChooser,
    refreshInfiniteCache,
    getExportParams,
    updateRowNodes,
    onRefresh
  }));

  const onSortChange = useCallback((sortModel) => {
    if (sortModel && sortModel.length > 0) {
      dispatch(updateGridColumns(sortModel));
    }
  }, [dispatch]);

  const onColumnSelectionsUpdate = useCallback((columns: ColDef[]) => {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  }, [dispatch]);

  const onColumnStateUpdate = useCallback((state: ColumnState[] = []) => {
    dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
  }, [dispatch]);

  const showLoadingOverlay = useCallback(() => {
    if (!silentRefreshRef.current) {
      setContentLoading(true);
    }
  }, []);

  // custom cell renderers (AccountCell, JobDevicesMiniBarChart, etc.) can
  // still be mounting after the row DOM/borders already exist, so a fixed
  // frame count isn't reliable - requestIdleCallback waits for the browser
  // to actually go quiet, meaning that mounting work has settled...
  const hideLoadingOverlay = useCallback(() => {
    if (silentRefreshRef.current) {
      silentRefreshRef.current = false;
    } else {
      const hide = () => setContentLoading(false);
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(hide, {timeout: 500});
      } else {
        requestAnimationFrame(() => requestAnimationFrame(hide));
      }
    }
  }, []);

  const gotoPage = useCallback((page: number) => {
    gridApiRef.current?.paginationGoToPage(page);
  }, []);

  const purgeCache = useCallback(() => {
    gridApiRef.current?.purgeInfiniteCache?.();
  }, []);

  const onGridReady = useCallback((api: AgGridType) => {
    gridApiRef.current = api;
  }, []);

  const {wrapLoadItems} = useJobGridPagination({activeTab, tabPaging, gotoPage, purgeCache});

  const wrappedLoadItems = useMemo(
    () => wrapLoadItems(loadItems, {onBeforeLoad: showLoadingOverlay, onSettled: hideLoadingOverlay}),
    [wrapLoadItems, loadItems, showLoadingOverlay, hideLoadingOverlay]
  );

  return (
    <div className={classes.gridContainer}>
      <Render.If condition={isContentLoading}>
        <div className={classes.loadingOverlay}>
          <IonSpinner />
        </div>
      </Render.If>
      <IonGrid ref={gridRef}
        paging={tabPaging}
        pageSize={paginationForJobTab.pageSize}
        onGridReady={onGridReady}
        onColumnChooserClose={function() { toggleColumnChooser(false); }}
        onRowDoubleClicked={onRowDoubleClicked}
        pageConstraintProps={{
          constraint : maxLimit,
          entity     : translate('common:entityLabels.job')
        }}
        onColumnChooserSave={onColumnSelectionsUpdate}
        defaultDefinitions={defaultDefinitions}
        sortModelConverter={sortModelConverter}
        checkboxesEnabled={false}
        columnDefinitions={columnDefinitions ?? []}
        showColumnChooser={showColumnChooser}
        attributeMappings={attributeMappings}
        onColumnsUpdated={onColumnStateUpdate}
        columnComponents={columnComponents}
        refreshRequired={refresh}
        onSortChange={onSortChange}
        augmentItems={constructGridRows}
        defaultSort={defaultSort}
        pageSizes={PAGE_SIZES}
        maxBlocksInCache={1}
        loadItems={wrappedLoadItems}
        usePreferedPageSize={false}
        showCustomPaginationBar
        paginationEnabled
        hideSubGroups
        viewSubGroups={viewSubGroups}
        toggleViewSubGroups={toggleSubGroups}
        getGridHeight={getGridHeight}
        omitHeader />
    </div>
  );
});

const getColumnDefinitions = () => {
  const columns = resolveDefinitions(getCommonColumnDefinitions());
  return ({
    desktop : columns,
    mobile  : columns
  });
};

/**
 * Returns column definitions for the JobSearchEngine grid.
 */
const getCommonColumnDefinitions = (): ColDef[] => {
  return [
    {
      headerName   : translate('jobsearchengine:grid.columns.id'),
      field        : 'jobId',
      cellRenderer : 'jobIdRenderer',
      maxWidth     : 120,
      sort         : null,
      sortable     : true,
      filter       : false,
      resizable    : true,
      unSortIcon   : true,
      hide         : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.name'),
      field      : 'name',
      sortable   : false,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.description'),
      field      : 'description',
      sortable   : false,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.tasks'),
      field      : 'tasks',
      sortable   : false,
      maxWidth   : 100,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.devices'),
      field      : 'devices',
      maxWidth   : 100,
      sortable   : false,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName         : translate('jobsearchengine:grid.columns.account'),
      field              : 'accountUI',
      cellRenderer       : 'accountName',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      unSortIcon         : true,
      hide               : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.creator'),
      field      : 'username',
      sortable   : true,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.createdOn'),
      field      : 'createdOn',
      sortable   : true,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : false
    },
    {
      headerName : translate('jobsearchengine:grid.columns.updatedOn'),
      field      : 'updatedOn',
      sortable   : true,
      filter     : false,
      resizable  : true,
      unSortIcon : true,
      hide       : true
    },
    {
      headerName   : translate('jobsearchengine:grid.columns.status'),
      field        : 'progressStatus',
      cellRenderer : 'statusRenderer',
      sortable     : true,
      filter       : false,
      resizable    : true,
      unSortIcon   : true,
      hide         : false
    },
    {
      headerName   : translate('jobsearchengine:grid.columns.details'),
      field        : 'details',
      cellRenderer : 'detailsRenderer',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false
    },
    {
      headerName   : translate('jobsearchengine:grid.columns.fileTypes'),
      field        : 'fileTypes',
      cellRenderer : 'fileTypesCell',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false
    }
  ];
};

export default JobSearchEngineGrid;
